import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const AddEmployee = ({ show, onHide, onEmployeeAdded }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    emp_code: '',
    first_name: '',
    last_name: '',
    gender: 'Male',
    dob: '',
    doj: '',
    dor: '',
    email: '',
    phone: '',
    pan_number: '',
    aadhaar_number: '',
    ESI_NO: '',
    PF_NO: '',
    UAN: '',
    bank_name: '',
    bank_account: '',
    ifsc_code: '',
    status: 'Active',
    dept_id: '',
    desig_id: '',
    unit_id: '',
    photo: null,
    address: ''
  });
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [units, setUnits] = useState([]);
  const [newDeptName, setNewDeptName] = useState('');
  const [newDesigName, setNewDesigName] = useState('');
  const [newUnitName, setNewUnitName] = useState('');
  const [showNewDept, setShowNewDept] = useState(false);
  const [showNewDesig, setShowNewDesig] = useState(false);
  const [showNewUnit, setShowNewUnit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [photoPreview, setPhotoPreview] = useState('');
  const [customFields, setCustomFields] = useState([]);
  const [customValues, setCustomValues] = useState({});

  const modalRef = useRef(null);
  const API_BASE = process.env.REACT_APP_API_BASE;

  // Disable body scroll when modal is open
  useEffect(() => {
    if (show) {
      // Store current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        // Restore scroll position
        const scrollY = parseInt(document.body.style.top || '0') * -1;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [show]);

  // Add keyboard escape handler
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && show) {
        onHide();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [show, onHide]);

  // Fetch departments, designations, units and custom fields
  useEffect(() => {
    if (show) {
      fetchDepartments();
      fetchDesignations();
      fetchUnits();
      fetchCustomFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/get_departments.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setDepartments(response.data.departments);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchDesignations = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/get_designations.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setDesignations(response.data.designations);
      }
    } catch (error) {
      console.error('Error fetching designations:', error);
    }
  };

  const fetchUnits = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/get_units.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setUnits(response.data.units);
      }
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  };

  const fetchCustomFields = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/get_custom_fields.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setCustomFields(response.data.fields);
        // Initialize custom values with empty strings
        const initialValues = {};
        response.data.fields.forEach(field => {
          initialValues[field.field_id] = '';
        });
        setCustomValues(initialValues);
      }
    } catch (error) {
      console.error('Error fetching custom fields:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCustomFieldChange = (fieldId, value) => {
    setCustomValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        photo: file
      }));

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const addNewDepartment = async () => {
    if (!newDeptName.trim()) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(`${API_BASE}/add_department.php`,
        { dept_name: newDeptName },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        await fetchDepartments();
        setFormData(prev => ({ ...prev, dept_id: response.data.dept_id }));
        setNewDeptName('');
        setShowNewDept(false);
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to add department');
    }
  };

  const addNewDesignation = async () => {
    if (!newDesigName.trim()) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(`${API_BASE}/add_designation.php`,
        { desig_name: newDesigName },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        await fetchDesignations();
        setFormData(prev => ({ ...prev, desig_id: response.data.desig_id }));
        setNewDesigName('');
        setShowNewDesig(false);
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to add designation');
    }
  };

  const addNewUnit = async () => {
    if (!newUnitName.trim()) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(`${API_BASE}/add_units.php`,
        { unit_name: newUnitName },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        await fetchUnits();
        setFormData(prev => ({ ...prev, unit_id: response.data.unit_id }));
        setNewUnitName('');
        setShowNewUnit(false);
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to add unit');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('authToken');
      const submitData = new FormData();

      // Append all form data
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData.append(key, formData[key]);
        }
      });

      // Add custom values to form data
      Object.keys(customValues).forEach(fieldId => {
        if (customValues[fieldId] !== '') {
          submitData.append(`custom_${fieldId}`, customValues[fieldId]);
        }
      });

      const response = await axios.post(`${API_BASE}/add_employee.php`,
        submitData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        onEmployeeAdded();
        onHide();
        // Reset form
        setFormData({
          emp_code: '',
          first_name: '',
          last_name: '',
          gender: 'Male',
          dob: '',
          doj: '',
          dor: '',
          email: '',
          phone: '',
          pan_number: '',
          aadhaar_number: '',
          ESI_NO: '',
          PF_NO: '',
          UAN: '',
          bank_name: '',
          bank_account: '',
          ifsc_code: '',
          status: 'Active',
          dept_id: '',
          desig_id: '',
          unit_id: '',
          photo: null,
          address: ''
        });
        setPhotoPreview('');
        // Reset custom values
        const resetCustomValues = {};
        customFields.forEach(field => {
          resetCustomValues[field.field_id] = '';
        });
        setCustomValues(resetCustomValues);
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to add employee');
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  const renderBasicInfoTab = () => (
    <div className="row g-3">
      <div className="col-md-6">
        <label className="form-label">Employee Code *</label>
        <input
          type="text"
          className="form-control"
          name="emp_code"
          value={formData.emp_code}
          onChange={handleChange}
          required
        />
      </div>

      <div className="col-md-6">
        <label className="form-label">Status</label>
        <select
          className="form-select"
          name="status"
          value={formData.status}
          onChange={handleChange}
        >
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
          <option value="Resigned">Resigned</option>
        </select>
      </div>

      <div className="col-md-6">
        <label className="form-label">First Name *</label>
        <input
          type="text"
          className="form-control"
          name="first_name"
          value={formData.first_name}
          onChange={handleChange}
          required
        />
      </div>

      <div className="col-md-6">
        <label className="form-label">Last Name *</label>
        <input
          type="text"
          className="form-control"
          name="last_name"
          value={formData.last_name}
          onChange={handleChange}
          required
        />
      </div>

      <div className="col-md-6">
        <label className="form-label">Gender</label>
        <select
          className="form-select"
          name="gender"
          value={formData.gender}
          onChange={handleChange}
        >
          <option value="Male">Male</option>
          <option value="Female">Female</option>
          <option value="Other">Other</option>
        </select>
      </div>

      <div className="col-md-6">
        <label className="form-label">Date of Birth</label>
        <input
          type="date"
          className="form-control"
          name="dob"
          value={formData.dob}
          onChange={handleChange}
        />
      </div>

      <div className="col-md-6">
        <label className="form-label">Department</label>
        <div className="input-group">
          <select
            className="form-select"
            name="dept_id"
            value={formData.dept_id}
            onChange={handleChange}
          >
            <option value="">Select Department</option>
            {departments.map(dept => (
              <option key={dept.dept_id} value={dept.dept_id}>
                {dept.dept_name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setShowNewDept(!showNewDept)}
          >
            <i className="bi bi-plus-lg"></i>
          </button>
        </div>
        {showNewDept && (
          <div className="mt-2">
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="New department name"
                value={newDeptName}
                onChange={(e) => setNewDeptName(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-success"
                onClick={addNewDepartment}
              >
                Add
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="col-md-6">
        <label className="form-label">Designation</label>
        <div className="input-group">
          <select
            className="form-select"
            name="desig_id"
            value={formData.desig_id}
            onChange={handleChange}
          >
            <option value="">Select Designation</option>
            {designations.map(desig => (
              <option key={desig.desig_id} value={desig.desig_id}>
                {desig.desig_name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setShowNewDesig(!showNewDesig)}
          >
            <i className="bi bi-plus-lg"></i>
          </button>
        </div>
        {showNewDesig && (
          <div className="mt-2">
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="New designation name"
                value={newDesigName}
                onChange={(e) => setNewDesigName(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-success"
                onClick={addNewDesignation}
              >
                Add
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="col-md-6">
        <label className="form-label">Unit/Group</label>
        <div className="input-group">
          <select
            className="form-select"
            name="unit_id"
            value={formData.unit_id}
            onChange={handleChange}
          >
            <option value="">Select Unit</option>
            {units.map(unit => (
              <option key={unit.unit_id} value={unit.unit_id}>
                {unit.unit_name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setShowNewUnit(!showNewUnit)}
          >
            <i className="bi bi-plus-lg"></i>
          </button>
        </div>
        {showNewUnit && (
          <div className="mt-2">
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="New unit name"
                value={newUnitName}
                onChange={(e) => setNewUnitName(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-success"
                onClick={addNewUnit}
              >
                Add
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="col-md-6">
        <label className="form-label">Date of Joining</label>
        <input
          type="date"
          className="form-control"
          name="doj"
          value={formData.doj}
          onChange={handleChange}
        />
      </div>

      <div className="col-md-6">
        <label className="form-label">Date of Resignation</label>
        <input
          type="date"
          className="form-control"
          name="dor"
          value={formData.dor}
          onChange={handleChange}
        />
      </div>
    </div>
  );

  const renderContactInfoTab = () => (
    <div className="row g-3">
      <div className="col-md-6">
        <label className="form-label">Email *</label>
        <input
          type="email"
          className="form-control"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
        />
      </div>

      <div className="col-md-6">
        <label className="form-label">Phone</label>
        <input
          type="tel"
          className="form-control"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
        />
      </div>

      <div className="col-12">
        <label className="form-label">Address</label>
        <textarea
          className="form-control"
          name="address"
          value={formData.address || ''}
          onChange={handleChange}
          rows="2"
          placeholder="Enter employee address"
        ></textarea>
      </div>

      <div className="col-md-6">
        <label className="form-label">PAN Number</label>
        <input
          type="text"
          className="form-control"
          name="pan_number"
          value={formData.pan_number}
          onChange={handleChange}
        />
      </div>

      <div className="col-md-6">
        <label className="form-label">Aadhaar Number</label>
        <input
          type="text"
          className="form-control"
          name="aadhaar_number"
          value={formData.aadhaar_number}
          onChange={handleChange}
        />
      </div>

      {/* Add new fields for ESI, PF, and UAN */}
      <div className="col-md-4">
        <label className="form-label">ESI Number</label>
        <input
          type="text"
          className="form-control"
          name="ESI_NO"
          value={formData.ESI_NO}
          onChange={handleChange}
        />
      </div>

      <div className="col-md-4">
        <label className="form-label">PF Number</label>
        <input
          type="text"
          className="form-control"
          name="PF_NO"
          value={formData.PF_NO}
          onChange={handleChange}
        />
      </div>

      <div className="col-md-4">
        <label className="form-label">UAN</label>
        <input
          type="text"
          className="form-control"
          name="UAN"
          value={formData.UAN}
          onChange={handleChange}
        />
      </div>
    </div>
  );

  const renderBankInfoTab = () => (
    <div className="row g-3">
      <div className="col-md-6">
        <label className="form-label">Bank Name</label>
        <input
          type="text"
          className="form-control"
          name="bank_name"
          value={formData.bank_name}
          onChange={handleChange}
        />
      </div>

      <div className="col-md-6">
        <label className="form-label">Account Number</label>
        <input
          type="text"
          className="form-control"
          name="bank_account"
          value={formData.bank_account}
          onChange={handleChange}
        />
      </div>

      <div className="col-12">
        <label className="form-label">IFSC Code</label>
        <input
          type="text"
          className="form-control"
          name="ifsc_code"
          value={formData.ifsc_code}
          onChange={handleChange}
        />
      </div>
    </div>
  );

  const renderPhotoTab = () => (
    <div className="row g-3">
      <div className="col-12">
        <label className="form-label">Employee Photo</label>
        <input
          type="file"
          className="form-control"
          accept="image/*"
          onChange={handleFileChange}
        />
        <div className="form-text">
          Supported formats: JPG, PNG, GIF. Max size: 2MB
        </div>
      </div>

      {photoPreview && (
        <div className="col-12">
          <div className="mt-3">
            <label className="form-label">Preview:</label>
            <div className="mt-2">
              <img
                src={photoPreview}
                alt="Preview"
                className="img-thumbnail"
                style={{ maxWidth: '200px', maxHeight: '200px' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderOtherValuesTab = () => (
    <div className="row g-3">
      {customFields.length > 0 ? (
        customFields.map(field => (
          <div key={field.field_id} className="col-md-6">
            <label className="form-label">{field.field_name}</label>
            {field.field_type === 'text' && (
              <input
                type="text"
                className="form-control"
                value={customValues[field.field_id] || ''}
                onChange={(e) => handleCustomFieldChange(field.field_id, e.target.value)}
              />
            )}
            {field.field_type === 'number' && (
              <input
                type="number"
                className="form-control"
                value={customValues[field.field_id] || ''}
                onChange={(e) => handleCustomFieldChange(field.field_id, e.target.value)}
              />
            )}
            {field.field_type === 'date' && (
              <input
                type="date"
                className="form-control"
                value={customValues[field.field_id] || ''}
                onChange={(e) => handleCustomFieldChange(field.field_id, e.target.value)}
              />
            )}
            {field.field_type === 'boolean' && (
              <select
                className="form-select"
                value={customValues[field.field_id] || ''}
                onChange={(e) => handleCustomFieldChange(field.field_id, e.target.value)}
              >
                <option value="">Select</option>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
            )}
            {field.field_type === 'select' && (
              <select
                className="form-select"
                value={customValues[field.field_id] || ''}
                onChange={(e) => handleCustomFieldChange(field.field_id, e.target.value)}
              >
                <option value="">Select</option>
                {field.field_options && field.field_options.split(',').map(option => (
                  <option key={option.trim()} value={option.trim()}>
                    {option.trim()}
                  </option>
                ))}
              </select>
            )}
          </div>
        ))
      ) : (
        <div className="col-12">
          <div className="alert alert-info text-center">
            <i className="bi bi-info-circle me-2"></i>
            No custom fields defined. Please contact administrator to add custom fields.
          </div>
        </div>
      )}
    </div>
  );

  // CSS for attractive tab background
  const tabStyles = `
    .nav-tabs .nav-link {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border: none;
      margin-right: 5px;
      border-radius: 8px 8px 0 0;
      transition: all 0.3s ease;
    }
    .nav-tabs .nav-link:hover {
      background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
      transform: translateY(-2px);
    }
    .nav-tabs .nav-link.active {
      background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
      color: white;
      font-weight: bold;
      box-shadow: 0 4px 15px rgba(0,0,0,0.2);
    }
  `;

  return (
    <>
      <style>{tabStyles}</style>
      {show && (
        <div
          className="modal-container"
          ref={modalRef}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1050,
            overflow: 'auto'
          }}
        >
          <div
            className="modal-backdrop fade show"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 1040
            }}
            onClick={onHide}
          ></div>

          <div
            className="modal-dialog modal-lg modal-dialog-centered"
            style={{
              position: 'relative',
              zIndex: 1055,
              maxHeight: '90vh',
              margin: '5vh auto',
              maxWidth: '900px'
            }}
          >
            <div className="modal-content border-0 shadow" style={{ maxHeight: '90vh' }}>
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  <i className="bi bi-person-plus me-2"></i>
                  Add New Employee
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={onHide}
                ></button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="modal-body" style={{ overflowY: 'auto', maxHeight: 'calc(90vh - 150px)' }}>
                  {error && (
                    <div className="alert alert-danger d-flex align-items-center">
                      <i className="bi bi-exclamation-triangle-fill me-2"></i>
                      <div>{error}</div>
                    </div>
                  )}

                  {/* Tabs Navigation */}
                  <ul className="nav nav-tabs mb-4">
                    <li className="nav-item">
                      <button
                        type="button"
                        className={`nav-link ${activeTab === 'basic' ? 'active' : ''}`}
                        onClick={() => setActiveTab('basic')}
                      >
                        <i className="bi bi-info-circle me-2"></i>
                        Basic Information
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        type="button"
                        className={`nav-link ${activeTab === 'contact' ? 'active' : ''}`}
                        onClick={() => setActiveTab('contact')}
                      >
                        <i className="bi bi-telephone me-2"></i>
                        Contact Information
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        type="button"
                        className={`nav-link ${activeTab === 'bank' ? 'active' : ''}`}
                        onClick={() => setActiveTab('bank')}
                      >
                        <i className="bi bi-bank me-2"></i>
                        Bank Information
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        type="button"
                        className={`nav-link ${activeTab === 'other' ? 'active' : ''}`}
                        onClick={() => setActiveTab('other')}
                      >
                        <i className="bi bi-list-check me-2"></i>
                        Other Values
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        type="button"
                        className={`nav-link ${activeTab === 'photo' ? 'active' : ''}`}
                        onClick={() => setActiveTab('photo')}
                      >
                        <i className="bi bi-camera me-2"></i>
                        Photo Upload
                      </button>
                    </li>
                  </ul>

                  {/* Tab Content */}
                  <div className="tab-content">
                    {activeTab === 'basic' && renderBasicInfoTab()}
                    {activeTab === 'contact' && renderContactInfoTab()}
                    {activeTab === 'bank' && renderBankInfoTab()}
                    {activeTab === 'other' && renderOtherValuesTab()}
                    {activeTab === 'photo' && renderPhotoTab()}
                  </div>
                </div>

                <div className="modal-footer">
                  <div className="me-auto">
                    {activeTab !== 'basic' && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => {
                          const prevTabs = ['basic', 'contact', 'bank', 'other', 'photo'];
                          const currentIndex = prevTabs.indexOf(activeTab);
                          if (currentIndex > 0) {
                            setActiveTab(prevTabs[currentIndex - 1]);
                          }
                        }}
                      >
                        <i className="bi bi-arrow-left me-2"></i>
                        Previous
                      </button>
                    )}
                  </div>

                  <div>
                    <button
                      type="button"
                      className="btn btn-secondary me-2"
                      onClick={onHide}
                      disabled={loading}
                    >
                      Cancel
                    </button>

                    {activeTab !== 'photo' ? (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          const nextTabs = ['basic', 'contact', 'bank', 'other', 'photo'];
                          const currentIndex = nextTabs.indexOf(activeTab);
                          if (currentIndex < nextTabs.length - 1) {
                            setActiveTab(nextTabs[currentIndex + 1]);
                          }
                        }}
                      >
                        Next
                        <i className="bi bi-arrow-right ms-2"></i>
                      </button>
                    ) : (
                      <button
                        type="submit"
                        className="btn btn-success"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Adding...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-check-lg me-2"></i>
                            Add Employee
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddEmployee;